/**
 * The Knock Mode and Lead Pipeline permission catalogue, and who holds what by default.
 *
 * ⚠️ THE DATABASE IS THE ENFORCEMENT; THIS IS THE CATALOGUE AND THE COURTESY.
 *
 * Every string here is also a row in `public.permissions`, seeded by
 * `20261003a7_knk_0_5_field_lead_permissions.sql` together with `DEFAULT_GRANTS`.
 * `fieldPermissions.test.ts` reads that migration and fails if the two disagree, so a
 * permission added here without a migration (or the reverse) cannot ship quietly.
 *
 * The shape is the one the Roles & Permissions editor (EPIC-RBAC-001, FR-9 to FR-11)
 * renders, so it can adopt this file unchanged:
 *
 *  - A **capability ladder** is ordered rungs over one noun (`lead.view` < `lead.edit` <
 *    `lead.manage`). Granting a rung grants every rung below it.
 *  - A **scope ladder** says whose records: Own, Their team, Everyone. Each rung above the
 *    floor is its own string, `<base>.team` and `<base>.all` for a scoped switch, or
 *    `<noun>.scope.team` / `<noun>.scope.all` for a noun with a capability ladder. This is
 *    the convention `project.view_team` / `project.view_all` already set. Everyone implies
 *    Their team, so both rungs are granted together.
 *  - A **destructive** permission is never part of a ladder (RBAC FR-10).
 *
 * "Everyone" means everyone in the current Account. No grant here crosses an Account;
 * sub-account roll-ups go through the sub-accounts mechanism.
 *
 * `system.override` satisfies every permission, as it does in `cost_holds_permission()`.
 * Superadmin is granted none of these directly.
 */

export type FieldPermissionArea = 'Field sales' | 'Leads' | 'Reporting';

export type FieldPermissionKind = 'switch' | 'capability' | 'scope' | 'destructive';

export interface FieldPermission {
  key: string;
  area: FieldPermissionArea;
  kind: FieldPermissionKind;
  /** What an admin reads on the role editor row. Never the key. */
  label: string;
  /** One line, written for an admin. Also stored as `permissions.description`. */
  description: string;
  /** Rendered in the role-change diff when the permission is added. */
  gained: string;
  /** Rendered in the role-change diff when the permission is removed. */
  lost: string;
}

export type Scope = 'own' | 'team' | 'all';

/** A scoped permission: the base string and the strings for each rung above its floor. */
export interface ScopeLadder {
  base: string;
  /** `own` when holding the base alone already reaches the holder's own records. */
  floor: 'own' | 'none';
  team: string;
  all: string;
}

export const SCOPE_LADDERS: readonly ScopeLadder[] = [
  { base: 'lead.view', floor: 'own', team: 'lead.scope.team', all: 'lead.scope.all' },
  { base: 'door.view', floor: 'own', team: 'door.scope.team', all: 'door.scope.all' },
  { base: 'territory.view', floor: 'own', team: 'territory.scope.team', all: 'territory.scope.all' },
  { base: 'knock.review', floor: 'none', team: 'knock.review.team', all: 'knock.review.all' },
  { base: 'field.live_view', floor: 'none', team: 'field.live_view.team', all: 'field.live_view.all' },
  { base: 'field.playback', floor: 'none', team: 'field.playback.team', all: 'field.playback.all' },
  { base: 'report.field', floor: 'none', team: 'report.field.team', all: 'report.field.all' },
  { base: 'report.statement', floor: 'own', team: 'report.statement.team', all: 'report.statement.all' },
];

export const CAPABILITY_LADDERS: Readonly<Record<string, readonly string[]>> = {
  lead: ['lead.view', 'lead.edit', 'lead.manage'],
  territory: ['territory.view', 'territory.manage'],
};

const p = (
  key: string,
  area: FieldPermissionArea,
  kind: FieldPermissionKind,
  label: string,
  description: string,
  gained: string,
  lost: string,
): FieldPermission => ({ key, area, kind, label, description, gained, lost });

export const FIELD_PERMISSIONS: readonly FieldPermission[] = [
  // ── Field sales ──────────────────────────────────────────────────────────
  p('knock.use', 'Field sales', 'switch', 'Use Knock Mode',
    'Knock Mode on the phone: start sessions, log doors and book appointments from a door.',
    'Can knock doors in the app, log what happened and book appointments from the door.',
    'Can no longer use Knock Mode on the phone.'),
  p('door.view', 'Field sales', 'scope', 'See their own doors',
    'See doors on the map. Their own doors at least; the scope decides whose else. Do Not Knock doors are always shown.',
    'Can see doors on the map.',
    'Can no longer see doors on the map, except Do Not Knock doors.'),
  p('door.scope.team', 'Field sales', 'scope', "See their team's doors",
    'Extends seeing doors to doors their team has worked.',
    "Can see their team's doors, not only their own.",
    "Can no longer see their team's doors."),
  p('door.scope.all', 'Field sales', 'scope', 'See every door in the account',
    'Extends seeing doors to every door in the account.',
    'Can see every door in the account.',
    'Can no longer see doors outside their team.'),
  p('door.edit', 'Field sales', 'switch', 'Add and move doors',
    'Add a door, move a door to the right house, and mark a door Do Not Knock.',
    'Can add doors, move them and mark them Do Not Knock.',
    'Can no longer add or move doors or mark them Do Not Knock.'),
  p('dnk.remove', 'Field sales', 'destructive', 'Remove Do Not Knock',
    'Take a door off Do Not Knock and import or remove registry lists. Every use is recorded.',
    'Can take doors off Do Not Knock, which is recorded.',
    'Can no longer take doors off Do Not Knock.'),
  p('knock.review', 'Field sales', 'scope', 'Review flagged knocks',
    'Confirm or restore knocks flagged by verification, and answer review requests. Needs a scope.',
    'Can review flagged knocks and review requests within their scope.',
    'Can no longer review flagged knocks.'),
  p('knock.review.team', 'Field sales', 'scope', "Review their team's flagged knocks",
    "Reviewing flagged knocks reaches their team's knocks.",
    "Can review their team's flagged knocks.",
    "Can no longer review their team's flagged knocks."),
  p('knock.review.all', 'Field sales', 'scope', 'Review every flagged knock in the account',
    'Reviewing flagged knocks reaches every knock in the account.',
    'Can review flagged knocks from anyone in the account.',
    'Can no longer review flagged knocks outside their team.'),
  p('field.team_lead', 'Field sales', 'switch', 'Lead a field team',
    "See the team strip in the app and share areas with their team from the phone.",
    'Can see the team strip in the app and share areas with their team.',
    'Can no longer see the team strip or share areas from the phone.'),
  p('field.live_view', 'Field sales', 'scope', 'See reps live',
    'See where reps are while they are in a knocking session. Every view is recorded and the rep is told. Needs a scope.',
    'Can see where reps in their scope are during a knocking session.',
    'Can no longer see where reps are during sessions.'),
  p('field.live_view.team', 'Field sales', 'scope', "See their team's reps live",
    'Seeing reps live reaches their own team.',
    'Can see their team live during sessions.',
    'Can no longer see their team live.'),
  p('field.live_view.all', 'Field sales', 'scope', 'See every rep in the account live',
    'Seeing reps live reaches every rep in the account.',
    'Can see every rep in the account live during sessions.',
    'Can no longer see reps outside their team live.'),
  p('field.playback', 'Field sales', 'scope', 'Replay past sessions',
    "Replay a rep's past session route. More sensitive than seeing reps live. Needs a scope.",
    'Can replay past session routes for reps in their scope.',
    'Can no longer replay past session routes.'),
  p('field.playback.team', 'Field sales', 'scope', "Replay their team's past sessions",
    "Replaying sessions reaches their team's sessions.",
    "Can replay their team's past sessions.",
    "Can no longer replay their team's past sessions."),
  p('field.playback.all', 'Field sales', 'scope', 'Replay every past session in the account',
    'Replaying sessions reaches every rep in the account.',
    'Can replay past sessions for anyone in the account.',
    'Can no longer replay sessions outside their team.'),
  p('territory.view', 'Field sales', 'capability', 'See their own areas and territories',
    'See areas and territories on the map.',
    'Can see areas and territories.',
    'Can no longer see areas and territories.'),
  p('territory.manage', 'Field sales', 'capability', 'Manage areas and territories',
    'Draw, split and assign areas and territories.',
    'Can draw, split and assign areas and territories.',
    'Can no longer change areas or territories.'),
  p('territory.scope.team', 'Field sales', 'scope', "Work with their team's areas and territories",
    "Reaches their team's areas and territories, not only their own.",
    "Can work with their team's areas and territories.",
    "Can no longer work with their team's areas and territories."),
  p('territory.scope.all', 'Field sales', 'scope', 'Work with every area and territory in the account',
    'Reaches every area and territory in the account.',
    'Can work with every area and territory in the account.',
    'Can no longer work with areas and territories outside their team.'),
  p('field.settings.manage', 'Field sales', 'switch', 'Change field settings',
    'Dispositions, quick buttons, verification thresholds, location policy text and knocking hours.',
    'Can change field settings: dispositions, verification, location policy and knocking hours.',
    'Can no longer change field settings.'),
  p('registration.manage', 'Field sales', 'switch', 'Record rep registrations and permits',
    'Record rep registrations and door-to-door permits.',
    'Can record rep registrations and permits.',
    'Can no longer record rep registrations and permits.'),

  // ── Leads ────────────────────────────────────────────────────────────────
  p('lead.view', 'Leads', 'capability', 'See their own leads',
    'See leads. Their own at least; the scope decides whose else.',
    'Can see leads.',
    'Can no longer see leads.'),
  p('lead.edit', 'Leads', 'capability', 'Work leads',
    'Edit leads, log calls and texts, and book appointments.',
    'Can edit leads, log contact and book appointments.',
    'Can no longer edit leads or book from them.'),
  p('lead.manage', 'Leads', 'capability', 'Manage leads',
    'Reassign, merge and recycle leads, and use bulk actions.',
    'Can reassign, merge and recycle leads and use bulk actions.',
    'Can no longer reassign, merge or bulk-change leads.'),
  p('lead.scope.team', 'Leads', 'scope', "Work with their team's leads",
    "Lead permissions reach their team's leads, not only their own.",
    "Can work with their team's leads.",
    "Can no longer work with their team's leads."),
  p('lead.scope.all', 'Leads', 'scope', 'Work with every lead in the account',
    'Lead permissions reach every lead in the account.',
    'Can work with every lead in the account.',
    'Can no longer work with leads outside their team.'),
  p('lead.delete', 'Leads', 'destructive', 'Delete leads',
    'Permanently delete a lead. Merging is usually the better choice.',
    'Can permanently delete leads.',
    'Can no longer delete leads.'),
  p('lead.convert', 'Leads', 'switch', 'Convert leads to projects',
    'Turn any lead in scope into a project. A closer can always convert their own appointments.',
    'Can convert any lead in their scope into a project.',
    'Can no longer convert leads they do not own.'),
  p('lead.unconvert', 'Leads', 'switch', 'Undo a conversion',
    'Reverse a conversion within the allowed window.',
    'Can undo a conversion within the allowed window.',
    'Can no longer undo conversions.'),
  p('lead.attribution.edit', 'Leads', 'switch', 'Change who gets credit for a lead',
    'Change the setter, closer and splits on a lead and settle credit disputes. Every change is recorded.',
    'Can change who gets credit for a lead and settle disputes, which is recorded.',
    'Can no longer change who gets credit for a lead.'),
  p('lead.import', 'Leads', 'switch', 'Import leads',
    'Run lead imports and undo an import batch.',
    'Can import leads and undo an import.',
    'Can no longer import leads.'),
  p('lead.export', 'Leads', 'switch', 'Export leads',
    'Export lead contact details. Every export is recorded; never available on the phone.',
    'Can export lead contact details, which is recorded.',
    'Can no longer export leads.'),
  p('lead.return.manage', 'Leads', 'switch', 'Handle vendor returns',
    'Confirm returns of bought leads to their vendor and record credits.',
    'Can confirm vendor returns and record credits.',
    'Can no longer handle vendor returns.'),
  p('lead.settings.manage', 'Leads', 'switch', 'Change lead settings',
    'Lead pipelines and stages, sources and costs, speed to lead, assignment rules, intake forms and keys.',
    'Can change lead pipelines, sources, assignment and intake settings.',
    'Can no longer change lead settings.'),
  p('consent.manage', 'Leads', 'switch', 'Edit consent wording',
    'Edit the consent text homeowners agree to and import numbers to Do Not Contact. Every change is recorded.',
    'Can edit consent wording and import to Do Not Contact, which is recorded.',
    'Can no longer edit consent wording.'),
  p('dnc.remove', 'Leads', 'destructive', 'Remove from Do Not Contact',
    'Take a phone number or email off Do Not Contact. Every use is recorded.',
    'Can take people off Do Not Contact, which is recorded.',
    'Can no longer take people off Do Not Contact.'),

  // ── Reporting ────────────────────────────────────────────────────────────
  p('report.field', 'Reporting', 'scope', 'See field reports',
    'Team board, scorecards, coaching and the knock-to-sale funnel. Needs a scope.',
    'Can see field reports for their scope.',
    'Can no longer see field reports.'),
  p('report.field.team', 'Reporting', 'scope', "See their team's field reports",
    'Field reports cover their team.',
    'Can see field reports for their team.',
    'Can no longer see field reports for their team.'),
  p('report.field.all', 'Reporting', 'scope', 'See field reports for everyone in the account',
    'Field reports cover the whole account.',
    'Can see field reports for the whole account.',
    'Can no longer see field reports beyond their team.'),
  p('report.lead_source', 'Reporting', 'switch', 'See lead source costs',
    'Cost per lead and return on spend by source and vendor. Commercially sensitive.',
    'Can see what each lead source costs and returns.',
    'Can no longer see lead source costs.'),
  p('report.statement', 'Reporting', 'scope', 'See their own setter and closer statements',
    'Statements of sets, sits and outcomes. Their own at least; the scope decides whose else.',
    'Can see setter and closer statements.',
    'Can no longer see setter and closer statements.'),
  p('report.statement.team', 'Reporting', 'scope', "See their team's setter and closer statements",
    "Statements cover their team's setters and closers.",
    "Can see their team's statements.",
    "Can no longer see their team's statements."),
  p('report.statement.all', 'Reporting', 'scope', "See everyone's setter and closer statements",
    'Statements cover everyone in the account.',
    "Can see everyone's statements in the account.",
    'Can no longer see statements beyond their team.'),
];

export const FIELD_PERMISSION_KEYS: readonly string[] = FIELD_PERMISSIONS.map((x) => x.key);

/** Phase 4 strings the catalogue deliberately does not seed yet. */
export const DEFERRED_PERMISSIONS: readonly string[] = [
  'contest.manage',
  'contest.prize.approve',
  'score.adjust',
  'compete.settings.manage',
];

// ─────────────────────────────────────────────────────────────────────────────
// Default grants (elicitation-manager-ops.md §7.2, plus field.team_lead)
// ─────────────────────────────────────────────────────────────────────────────

const LEAD = {
  editOwn: ['lead.view', 'lead.edit'],
  manageTeam: ['lead.view', 'lead.edit', 'lead.manage', 'lead.scope.team'],
  manageAll: ['lead.view', 'lead.edit', 'lead.manage', 'lead.scope.team', 'lead.scope.all'],
  viewAll: ['lead.view', 'lead.scope.team', 'lead.scope.all'],
};
const DOORS_ALL = ['door.view', 'door.scope.team', 'door.scope.all'];
const TERRITORY = {
  viewOwn: ['territory.view'],
  viewAll: ['territory.view', 'territory.scope.team', 'territory.scope.all'],
  manageTeam: ['territory.view', 'territory.manage', 'territory.scope.team'],
  manageAll: ['territory.view', 'territory.manage', 'territory.scope.team', 'territory.scope.all'],
};
const team = (base: string) => [base, `${base}.team`];
const all = (base: string) => [base, `${base}.team`, `${base}.all`];

const ADMIN = [
  ...LEAD.manageAll, 'lead.delete', 'lead.convert', 'lead.unconvert', 'lead.attribution.edit',
  'lead.import', 'lead.export', 'lead.return.manage', 'lead.settings.manage', 'consent.manage', 'dnc.remove',
  'knock.use', ...DOORS_ALL, 'door.edit', 'dnk.remove', ...all('knock.review'), 'field.team_lead',
  ...all('field.live_view'), ...all('field.playback'), ...TERRITORY.manageAll,
  'field.settings.manage', 'registration.manage',
  ...all('report.field'), 'report.lead_source', ...all('report.statement'),
];

const SALES_MANAGER = [
  ...LEAD.manageTeam, 'lead.convert', 'lead.unconvert', 'lead.attribution.edit',
  'knock.use', ...DOORS_ALL, 'door.edit', ...team('knock.review'), 'field.team_lead',
  ...team('field.live_view'), ...team('field.playback'), ...TERRITORY.manageTeam, 'registration.manage',
  ...team('report.field'), ...team('report.statement'),
];

/**
 * Keyed by global role NAME, which is how the migration resolves them. Internal PM,
 * Installer PM, Site Surveyor, Investor, Agency Admin and Superadmin get nothing here on
 * purpose: see §7.2's notes. Fair Seas' admins reach Admin through `user_roles`.
 *
 * ⚠️ Team Owner is NOT an account owner. §7.2 assumed it was and granted Admin's set; the
 * 17 live Team Owners hold `team.own` and own a team inside an account. Capped at their team
 * (Allan, 2026-10-03), corrected live by `20261003b7`. Granting it Everyone would have shown
 * each of them every pin in Solerro, contact details included.
 */
export const DEFAULT_GRANTS: Readonly<Record<string, readonly string[]>> = {
  'Sales Rep': [...LEAD.editOwn, 'knock.use', ...DOORS_ALL, 'door.edit', ...TERRITORY.viewOwn, 'report.statement'],
  'Sales Manager': SALES_MANAGER,
  Admin: ADMIN,
  'Team Owner': [...SALES_MANAGER, 'lead.import'],
  'Team Admin': [
    ...LEAD.manageTeam, 'lead.convert', 'lead.import', 'lead.return.manage', 'lead.settings.manage',
    ...DOORS_ALL, 'field.team_lead', ...TERRITORY.viewAll, 'registration.manage', ...team('report.field'),
  ],
  'Biz Dev Rep': [...LEAD.editOwn, 'knock.use', ...DOORS_ALL, 'door.edit', ...TERRITORY.viewOwn, 'report.statement'],
  Marketer: [...LEAD.viewAll, 'lead.import', 'lead.return.manage', 'report.lead_source'],
  Finance: [...LEAD.viewAll, 'lead.return.manage', 'report.lead_source', ...all('report.statement')],
  'Read Only': [...LEAD.viewAll, ...DOORS_ALL, ...TERRITORY.viewAll, ...all('report.field')],
};

// ─────────────────────────────────────────────────────────────────────────────
// Reading what someone holds
// ─────────────────────────────────────────────────────────────────────────────

export const SYSTEM_OVERRIDE = 'system.override';

export function holdsFieldPermission(held: readonly string[] | null | undefined, key: string): boolean {
  if (!held || held.length === 0) return false;
  return held.includes(key) || held.includes(SYSTEM_OVERRIDE);
}

/**
 * How far a scoped permission reaches, or null when it is not held at all.
 * A rung without its base is not a grant: the editor always grants the base with it.
 */
export function fieldScope(held: readonly string[] | null | undefined, base: string): Scope | null {
  const ladder = SCOPE_LADDERS.find((l) => l.base === base);
  if (!ladder || !holdsFieldPermission(held, base)) return null;
  if (holdsFieldPermission(held, ladder.all)) return 'all';
  if (holdsFieldPermission(held, ladder.team)) return 'team';
  return ladder.floor === 'own' ? 'own' : null;
}
